import { useEffect, useRef, useState } from "react";
import { Eraser, PenLine } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, fileToBase64 } from "../api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";

const CHECKLIST = ["Carrosserie", "Pare-chocs", "Vitres / pare-brise", "Pneus", "Jantes", "Phares", "Intérieur", "Climatisation", "Accessoires"];
const STATES = ["OK", "Rayure", "Impact", "Usure", "Manquant"];

export default function InspectionDialog({ rental, type, open, onClose, onSaved }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [checklist, setChecklist] = useState({});
  const [form, setForm] = useState({ mileage: "", fuel_level: "", notes: "" });
  const [photos, setPhotos] = useState([]);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    if (open) {
      setChecklist(Object.fromEntries(CHECKLIST.map((c) => [c, "OK"])));
      setForm({ mileage: "", fuel_level: "", notes: "" });
      setPhotos([]);
      setHasSignature(false);
    }
  }, [open]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return [src.clientX - rect.left, src.clientY - rect.top];
  };
  const startDraw = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#0F172A";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(...pos(e));
  };
  const draw = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(...pos(e));
    ctx.stroke();
    setHasSignature(true);
  };
  const endDraw = () => { drawing.current = false; };
  const clearSign = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasSignature(false);
  };

  const submit = async () => {
    if (!hasSignature) return toast.error("Signature du client requise");
    try {
      await api.post(`/rentals/${rental.id}/inspections`, {
        type,
        checklist,
        mileage: form.mileage ? Number(form.mileage) : null,
        fuel_level: form.fuel_level,
        notes: form.notes,
        photos,
        signature: canvasRef.current.toDataURL("image/png"),
      });
      toast.success(`État des lieux ${type === "depart" ? "de départ" : "de retour"} enregistré`);
      onClose();
      onSaved?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>État des lieux — {type === "depart" ? "Départ" : "Retour"} · {rental?.rental_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {CHECKLIST.map((item) => (
              <div key={item} className="rounded-lg border border-slate-200 p-2">
                <p className="mb-1 text-xs font-semibold text-slate-600">{item}</p>
                <Select value={checklist[item]} onValueChange={(v) => setChecklist({ ...checklist, [item]: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`checklist-${item.toLowerCase().replace(/[^a-z]/g, "-")}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Kilométrage</Label><Input type="number" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} data-testid="inspection-km" /></div>
            <div><Label>Niveau carburant</Label><Input value={form.fuel_level} onChange={(e) => setForm({ ...form, fuel_level: e.target.value })} placeholder="ex: 3/4" data-testid="inspection-fuel" /></div>
          </div>
          <div>
            <Label>Photos (rayures, impacts…)</Label>
            <Input type="file" accept="image/*" multiple data-testid="inspection-photos"
              onChange={async (e) => setPhotos(await Promise.all([...(e.target.files || [])].slice(0, 6).map(fileToBase64)))} />
            {photos.length > 0 && (
              <div className="mt-2 flex gap-2">{photos.map((p, i) => <img key={i} src={p} alt="" className="h-16 w-20 rounded object-cover" />)}</div>
            )}
          </div>
          <div><Label>Observations</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <Label className="flex items-center gap-1"><PenLine className="h-3.5 w-3.5" /> Signature du client</Label>
              <button type="button" onClick={clearSign} className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-500" data-testid="clear-signature"><Eraser className="h-3.5 w-3.5" /> Effacer</button>
            </div>
            <canvas ref={canvasRef} width={560} height={140}
              className="w-full touch-none rounded-lg border-2 border-dashed border-slate-300 bg-slate-50"
              onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
              onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
              data-testid="signature-canvas" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} data-testid="inspection-submit">Signer et enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
